import * as React from "react";
import { useAutoLogout } from "../../_shared/hooks/useAutoLogout";
import { useNavigate } from "react-router-dom";
import {useLocation, Navigate} from "react-router-dom";
import {useAuth} from "../../_auth/useAuth";
import {AuthRolesType} from "../../_appData";

interface RequireAuthProps {
    children: JSX.Element;
    authRoles?: Array<AuthRolesType>;
}


export const RequireAuth = ({ children, authRoles }: RequireAuthProps) => {
    const separatorChars = /[,;|]+/;
    const { isAuthenticated, isLoading, user, logout } = useAuth();
    let location = useLocation();
    const navigate = useNavigate();

    // Use the custom auto-logout hook
    useAutoLogout(() => {
        if (isAuthenticated && user) {
            logout({ localOnly: true });
            navigate('/session-expired', { replace: true });
        }
    });

    // Wait for authentication check to complete
    if (isLoading) {
        return <div>Loading...</div>;
    }

    if (!isAuthenticated || !user) {
        // not logged in, redirect to login page, but save the current location they were
        // trying to go to when they were redirected. This allows us to send them
        // along to that page after they login, which is a nicer user experience
        // than dropping them off on the home page.
        return <Navigate to="/login" state={{returnTo: location}} replace />
    }

    // check if route has authorized roles.
    // if so, check user has required privilege
    let isAuthorized = false;
    if (authRoles) {
        const arrRoles = authRoles.map((role: string) => role.toLowerCase());
        if (user && user.privileges) {
            for (let priv of user.privileges) {
                if (arrRoles.indexOf(priv.toLowerCase()) > -1) {
                    isAuthorized = true;
                    break;
                }
            }
        }
    } else {
        isAuthorized = true;
    }

    if (!isAuthorized) {
        // Redirect them to the /unauthorized page.
        return <Navigate to="/unauthorized" />;
    }

    return children;
}